import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LuCalendarCheck as LuCalendarCheckIcon } from "react-icons/lu";
import ContactForm from "./ContactForm";
import FormSubmittedModal from "./FormSubmittedModal";
import "./ContactFormModal.css";

// react-icons v5 typings don't satisfy JSX element checks on TS 4.4
const LuCalendarCheck = LuCalendarCheckIcon as unknown as React.FC<{
  className?: string;
  "aria-hidden"?: boolean;
}>;

export type ContactModalVariant = "partnership" | "outreach" | "volunteer";

const variants: Record<
  ContactModalVariant,
  {
    title: string;
    desc: string;
    placeholder: string;
    icon: React.ReactNode;
  }
> = {
  partnership: {
    title: "Partnership & Booking",
    desc: "For event invitations, collaborations, or ministry partnerships.",
    placeholder: "Share your message...",
    icon: <span aria-hidden="true">📧</span>,
  },
  outreach: {
    title: "Relief and Outreach",
    desc: "Share your needs so we can explore how to help you.",
    placeholder: "Let’s hear your needs...",
    icon: <LuCalendarCheck className="contact-modal-icon-svg" aria-hidden />,
  },
  volunteer: {
    title: "Volunteer your Time",
    desc: "Tell us a little about yourself and how you’d love to serve.",
    placeholder: "We would love to know what sparked your interest.",
    icon: <LuCalendarCheck className="contact-modal-icon-svg" aria-hidden />,
  },
};

interface ContactFormModalProps {
  variant: ContactModalVariant | null;
  onClose: () => void;
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({
  variant,
  onClose,
}) => {
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!variant || submitted) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [variant, submitted, onClose]);

  if (!variant) return null;
  const { title, desc, placeholder, icon } = variants[variant];

  // Once sent, the form dialog gives way to the confirmation
  if (submitted)
    return (
      <FormSubmittedModal
        type={variant}
        onClose={() => {
          setSubmitted(false);
          onClose();
        }}
      />
    );

  // Portal out so transformed (framer-motion) parents don't trap the fixed overlay
  return createPortal(
    <div className="contact-modal-backdrop" onClick={onClose}>
      <div
        className="contact-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="contact-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          &#10005;
        </button>

        <div className="contact-modal-heading">
          <span className="contact-modal-icon">{icon}</span>
          <h2 id="contact-modal-title">{title}</h2>
        </div>
        <p className="contact-modal-desc">{desc}</p>

        <ContactForm
          key={variant}
          type={variant}
          messagePlaceholder={placeholder}
          showInterests={variant === "volunteer"}
          onSuccess={() => setSubmitted(true)}
        />
      </div>
    </div>,
    document.body
  );
};

export default ContactFormModal;
